import React, { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import {
  listSupportTickets,
  updateSupportTicketStatus,
  deleteSupportTicket,
} from "../../apis/supportService";
import { RefreshCw, X, Plus, Loader2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Paperclip, Maximize2 } from "lucide-react";
import toast from "react-hot-toast";
import { TicketAttachmentImage } from "./TicketAttachmentImage";
import { Link, useLocation, useNavigate } from "react-router-dom";

// Sorting options for user view
const SORT_OPTIONS = [
  { value: "updatedAt_DESC", label: "Last Updated (Newest)", field: "updatedAt", order: "DESC" },
  { value: "updatedAt_ASC", label: "Last Updated (Oldest)", field: "updatedAt", order: "ASC" },
  { value: "createdAt_DESC", label: "Date Created (Newest)", field: "createdAt", order: "DESC" },
  { value: "createdAt_ASC", label: "Date Created (Oldest)", field: "createdAt", order: "ASC" },
  { value: "title_ASC", label: "Title (A-Z)", field: "title", order: "ASC" },
  { value: "title_DESC", label: "Title (Z-A)", field: "title", order: "DESC" },
  { value: "category_ASC", label: "Category (A-Z)", field: "category", order: "ASC" },
  { value: "category_DESC", label: "Category (Z-A)", field: "category", order: "DESC" },
  { value: "status_ASC", label: "Status (A-Z)", field: "status", order: "ASC" },
  { value: "status_DESC", label: "Status (Z-A)", field: "status", order: "DESC" },
];

const UserSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pageInfo, setPageInfo] = useState({ page: 1, pages: 1, total: 0, limit: 10 });
  const [search, setSearch] = useState("");
  const [detailsModal, setDetailsModal] = useState(null);
  const [attachmentIndex, setAttachmentIndex] = useState(0);
  // Full-screen attachment viewer state (opened via the "Full Screen" button)
  const [fullscreenAttachment, setFullscreenAttachment] = useState(false);
  const [queryApplied, setQueryApplied] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [deletingTicketId, setDeletingTicketId] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [reopenModal, setReopenModal] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenError, setReopenError] = useState("");
  const reopenInputRef = useRef(null);
  const deleteInputRef = useRef(null);

  useEffect(() => {
    if (reopenModal) {
      window.scrollTo(0, 0);
      const timer = setTimeout(() => {
        reopenInputRef.current?.focus({ preventScroll: true });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [reopenModal]);

  useEffect(() => {
    if (deleteConfirmModal) {
      window.scrollTo(0, 0);
      const timer = setTimeout(() => {
        deleteInputRef.current?.focus({ preventScroll: true });
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [deleteConfirmModal]);
  const [sort, setSort] = useState(() => {
    try {
      const saved = localStorage.getItem("userSupportTickets_sort");
      return saved || "updatedAt_DESC";
    } catch {
      return "updatedAt_DESC";
    }
  });
  const location = useLocation();
  const navigate = useNavigate();

  // Save sort to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("userSupportTickets_sort", sort);
    } catch {}
  }, [sort]);

  const handleUpdateStatus = async (ticketId, status) => {
    setUpdatingStatusId(ticketId);
    try {
      await updateSupportTicketStatus(ticketId, status);
      toast.success(`Ticket status updated to ${status}`);
      await loadTickets();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleOpenReopenModal = (ticket) => {
    setReopenModal(ticket);
    setReopenReason("");
    setReopenError("");
  };

  const handleConfirmReopen = async () => {
    if (!reopenReason.trim()) {
      setReopenError("Please provide a reason for reopening this ticket.");
      return;
    }
    const ticketId = reopenModal.ticketId;
    const reasonText = reopenReason.trim();
    setUpdatingStatusId(ticketId);
    try {
      await updateSupportTicketStatus(ticketId, {
        status: "OPEN",
        reason: reasonText,
        note: reasonText,
      });
      toast.success("Ticket reopened successfully");
      setReopenModal(null);
      setReopenReason("");
      setReopenError("");
      await loadTickets();
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || "Failed to reopen ticket");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleOpenDeleteModal = (ticket) => {
    setDeleteConfirmModal(ticket);
    setDeleteReason("");
    setDeleteError("");
  };

  const handleConfirmDelete = async () => {
    if (!deleteReason.trim()) {
      setDeleteError("Please provide a reason for closing this ticket.");
      return;
    }
    const ticketId = deleteConfirmModal.ticketId;
    const reasonText = deleteReason.trim();
    setDeletingTicketId(ticketId);
    try {
      await deleteSupportTicket(ticketId, { reason: reasonText, note: reasonText });
      toast.success("Ticket deleted successfully");
      setDeleteConfirmModal(null);
      setDeleteReason("");
      setDeleteError("");
      await loadTickets();
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || "Failed to delete ticket");
    } finally {
      setDeletingTicketId(null);
    }
  };

  const openDetailsModal = (ticket) => {
    setDetailsModal(ticket);
    setAttachmentIndex(0);
    setFullscreenAttachment(false);
  };

  const loadTickets = async (overrides = {}) => {
    setLoading(true);
    try {
      const nextPage = overrides.page ?? page;
      const nextLimit = overrides.limit ?? limit;
      const nextSearch = overrides.search ?? search;
      const [sortBy, sortOrder] = sort.split("_");
      const params = {
        limit: nextLimit,
        page: nextPage,
        sortBy: sortBy || "updatedAt",
        sortOrder: sortOrder || "DESC",
      };
      if (nextSearch?.trim()) params.search = nextSearch.trim();
      const response = await listSupportTickets(params);
      setTickets(response?.items || []);
      if (response?.pagination) setPageInfo(response.pagination);
    } catch (error) {
      console.error("Failed to load personal tickets", error);
      toast.error("Unable to load your support tickets right now");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [page, limit, sort]);

  // If URL has ?ticketid=, apply it to search and then remove it after first load
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search || "");
      const ticketIdParam = params.get("ticketid");
      if (ticketIdParam && !queryApplied) {
        setPage(1);
        setSearch(ticketIdParam);
        setQueryApplied(true);
        loadTickets({ page: 1, search: ticketIdParam });
      }
    } catch (_) {}
  }, [location.search, queryApplied]);

  // After load completes once with query applied, strip ticketid param from URL
  useEffect(() => {
    if (!queryApplied || loading) return;
    const params = new URLSearchParams(location.search || "");
    if (params.has("ticketid")) {
      params.delete("ticketid");
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true }
      );
    }
  }, [queryApplied, loading, location.pathname, location.search, navigate]);

  // Full-screen attachment viewer: Escape to close + Left/Right arrows to navigate
  useEffect(() => {
    if (!fullscreenAttachment) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setFullscreenAttachment(false);
      } else if (e.key === "ArrowLeft") {
        setAttachmentIndex((index) => {
          const total = detailsModal?.attachments?.length || 1;
          return (index - 1 + total) % total;
        });
      } else if (e.key === "ArrowRight") {
        setAttachmentIndex((index) => {
          const total = detailsModal?.attachments?.length || 1;
          return (index + 1) % total;
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    // Prevent body scroll while fullscreen viewer is open
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [fullscreenAttachment, detailsModal]);

  const handleSortChange = (newSort) => {
    setPage(1);
    setSort(newSort);
  };

  // Sortable header component
  const SortableHeader = ({ label, sortField, currentSort, className = "" }) => {
    const [currentSortBy, currentSortOrder] = currentSort.split("_");
    const isActive = currentSortBy === sortField;
    const isAsc = currentSortOrder === "ASC";

    return (
      <th
        className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 cursor-pointer hover:bg-gray-100 ${className}`}
        onClick={() => {
          const newSort = isActive && isAsc ? `${sortField}_DESC` : `${sortField}_ASC`;
          handleSortChange(newSort);
        }}
      >
        <div className="flex items-center gap-1">
          {label}
          <div className="flex flex-col -space-y-1">
            <ChevronUp
              className={`h-2 w-2 ${isActive && isAsc ? "text-gray-700" : "text-gray-400"}`}
            />
            <ChevronDown
              className={`h-2 w-2 ${isActive && !isAsc ? "text-gray-700" : "text-gray-400"}`}
            />
          </div>
        </div>
      </th>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">My Support Tickets</h2>
          <p className="text-sm text-gray-600">Track the status of your open support requests.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/support-ticket"
            className="inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-teal-600 p-2 text-sm font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:w-auto"
          >
            <Plus className="h-4 w-4" /> New Ticket
          </Link>
          <button
            type="button"
            onClick={loadTickets}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-300 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Mobile search + sort */}
      <div className="md:hidden space-y-2">
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Ticket ID, title, description"
            className="h-9 w-full rounded-md border border-gray-200 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {search?.trim() ? (
            <button
              type="button"
              onClick={() => { setSearch(""); setPage(1); loadTickets({ page: 1, search: "" }); }}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs"
            >
              Clear
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => { setPage(1); loadTickets({ page: 1, search }); }}
            className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs"
          >
            Search
          </button>
        </div>

        {/* Mobile Sort Dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sort By</label>
          <select
            value={sort}
            onChange={(e) => handleSortChange(e.target.value)}
            className="h-8 w-full rounded-md border border-gray-200 px-2 text-sm focus:border-blue-500 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Mobile Top Pagination */}
        {!loading && (
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs text-gray-600 shadow-sm">
            <div>Page {pageInfo.page} / {pageInfo.pages || 1} • {pageInfo.total} total</div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pageInfo.page <= 1}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage(p => Math.min(pageInfo.pages || 1, p + 1))}
                disabled={pageInfo.page >= (pageInfo.pages || 1)}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="hidden md:block rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-2">
          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); }}
              placeholder="Search Ticket ID, title, description"
              className="h-9 w-64 rounded-md border border-gray-200 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
            <button
              type="button"
              onClick={() => { setPage(1); loadTickets({ page: 1, search }); }}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs"
            >
              Search
            </button>
            {search?.trim() && (
              <button
                type="button"
                onClick={() => { setSearch(""); setPage(1); loadTickets({ page: 1, search: "" }); }}
                className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                aria-label="Clear search"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Page {pageInfo.page} of {pageInfo.pages} • {pageInfo.total} total</span>
            <select value={limit} onChange={(e) => { setPage(1); setLimit(Number(e.target.value) || 10); }} className="h-8 rounded-md border border-gray-200 px-2 text-xs">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pageInfo.page <= 1} className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50">Prev</button>
            <button type="button" onClick={() => setPage(p => Math.min(pageInfo.pages || 1, p + 1))} disabled={pageInfo.page >= (pageInfo.pages || 1)} className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs disabled:opacity-50">Next</button>
          </div>
        </div>

        {/* Desktop Sort Dropdown */}
        <div className="flex items-center justify-end gap-2 border-b border-gray-100 px-4 py-1.5 bg-gray-50">
          <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Sort By</label>
          <select
            value={sort}
            onChange={(e) => handleSortChange(e.target.value)}
            className="h-7 rounded-md border border-gray-200 px-2 text-xs focus:border-blue-500 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <table className="min-w-full table-fixed divide-y divide-gray-200">
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[20%]" />
              <col className="w-[12%]" />
              <col className="w-[10%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
              <col className="w-[16%]" />
            </colgroup>
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Ticket ID</th>
                <SortableHeader label="Summary" sortField="title" currentSort={sort} className="px-4 py-3" />
                <SortableHeader label="Category" sortField="category" currentSort={sort} className="px-4 py-3" />
                <SortableHeader label="Status" sortField="status" currentSort={sort} className="px-4 py-3" />
                <SortableHeader label="Created At" sortField="createdAt" currentSort={sort} className="px-4 py-3" />
                <SortableHeader label="Updated At" sortField="updatedAt" currentSort={sort} className="px-4 py-3" />
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {tickets.map((ticket) => (
                <tr key={ticket.ticketId} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3.5 text-sm font-mono text-gray-700">{formatDisplayTicketId(ticket.createdAt, ticket.ticketId)}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-700">
                    <div className="font-bold text-gray-900 leading-snug">{(ticket.title || '-')}</div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-700">
                    <div className="text-xs text-gray-600 font-medium uppercase">{ticket.category || "-"}</div>
                  </td>
                  <td className="px-4 py-3.5 whitespace-nowrap">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusStyle(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(ticket.createdAt)}
                  </td>
                  <td className="px-4 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                    {formatDate(ticket.updatedAt)}
                  </td>
                  <td className="px-4 py-3.5 text-right whitespace-nowrap">
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 transition-colors"
                        onClick={() => openDetailsModal(ticket)}
                      >
                        View details
                      </button>
                      {(ticket.status === "CLOSED" || ticket.status === "RESOLVED") && (
                        <button
                          type="button"
                          disabled={updatingStatusId === ticket.ticketId}
                          onClick={() => handleOpenReopenModal(ticket)}
                          className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200 disabled:opacity-60 transition-colors"
                        >
                          {updatingStatusId === ticket.ticketId ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : null}
                          Reopen
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={deletingTicketId === ticket.ticketId}
                        onClick={() => handleOpenDeleteModal(ticket)}
                        className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-200 disabled:opacity-60 transition-colors"
                      >
                        {deletingTicketId === ticket.ticketId ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : null}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(!tickets || tickets.length === 0) && !loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                    You haven't submitted any support tickets yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {loading && (
            <div className="flex items-center justify-center px-4 py-6 text-sm text-gray-500">
              Loading...
            </div>
          )}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden grid gap-3">
        {tickets.map((ticket) => (
          <div key={ticket.ticketId} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusStyle(ticket.status)}`}>{ticket.status}</span>
              <div className="text-xs text-gray-500 text-right">
                <div><span className="text-gray-400">Created:</span> {formatDate(ticket.createdAt)}</div>
                <div><span className="text-gray-400">Updated:</span> {formatDate(ticket.updatedAt)}</div>
              </div>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              ID: <span className="font-mono">{formatDisplayTicketId(ticket.createdAt, ticket.ticketId)}</span>
            </div>
            <div className="mt-2 text-sm font-bold text-gray-900">{(ticket.title || '-')}</div>
            <div className="mt-1 text-xs uppercase text-gray-500">{ticket.category}</div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200"
                onClick={() => openDetailsModal(ticket)}
              >
                View details
              </button>
              {(ticket.status === "CLOSED" || ticket.status === "RESOLVED") && (
                <button
                  type="button"
                  disabled={updatingStatusId === ticket.ticketId}
                  onClick={() => handleOpenReopenModal(ticket)}
                  className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200 disabled:opacity-60"
                >
                  {updatingStatusId === ticket.ticketId ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : null}
                  Reopen
                </button>
              )}
              <button
                type="button"
                disabled={deletingTicketId === ticket.ticketId}
                onClick={() => handleOpenDeleteModal(ticket)}
                className="inline-flex items-center gap-1 rounded-md bg-rose-100 px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-200 disabled:opacity-60"
              >
                {deletingTicketId === ticket.ticketId ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : null}
                Delete
              </button>
            </div>
          </div>
        ))}
        {(!tickets || tickets.length === 0) && !loading && (
          <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500">You haven't submitted any support tickets yet.</div>
        )}
        {!loading && (
          <div className="flex items-center justify-between px-1 py-2 text-xs text-gray-600">
            <div>Page {pageInfo.page} / {pageInfo.pages}</div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pageInfo.page <= 1} className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-50">Prev</button>
              <button type="button" onClick={() => setPage(p => Math.min(pageInfo.pages || 1, p + 1))} disabled={pageInfo.page >= (pageInfo.pages || 1)} className="rounded-md border border-gray-200 bg-white px-2 py-1 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Outlook-style ticket inspector */}
      {detailsModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDetailsModal(null)} />
          <div className="relative z-10 flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-3">
                <h3 className="text-base font-semibold text-gray-900">
                  Ticket #{formatDisplayTicketId(detailsModal.createdAt, detailsModal.ticketId)}
                </h3>
                {(detailsModal.status === "CLOSED" || detailsModal.status === "RESOLVED") && (
                  <button
                    type="button"
                    disabled={updatingStatusId === detailsModal.ticketId}
                    onClick={() => {
                      const t = detailsModal;
                      setDetailsModal(null);
                      handleOpenReopenModal(t);
                    }}
                    className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-200 disabled:opacity-60 transition-colors"
                  >
                    Reopen
                  </button>
                )}
              </div>
              <button
                className="rounded-full p-1 text-gray-500 hover:bg-gray-100"
                onClick={() => setDetailsModal(null)}
                aria-label="Close details"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
            <div className={`${Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? "grid md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]" : ""}`}>
              <div className={`p-5 text-left text-sm ${Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? "space-y-4 border-b border-gray-200 md:border-b-0 md:border-r" : "flex flex-wrap items-start gap-x-10 gap-y-4 [&>div]:min-w-0 [&>div]:max-w-full [&>div]:break-words"}`}>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Subject</div>
                <div className="mt-1 font-medium text-gray-900">{detailsModal.title || '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Category</div>
                <div className="mt-1 font-medium text-gray-900">{detailsModal.category || '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Priority</div>
                <div className="mt-1 font-medium text-gray-900">{detailsModal.priority || '-'}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Status</div>
                <span className={`mt-1 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusStyle(detailsModal.status)}`}>
                  {detailsModal.status}
                </span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Created By</div>
                <div className="mt-1 font-medium text-gray-900">
                  {[detailsModal.requester?.profile?.firstName, detailsModal.requester?.profile?.lastName].filter(Boolean).join(" ").trim() ||
                    detailsModal.requesterName ||
                    detailsModal.requester?.name ||
                    detailsModal.requester?.email ||
                    'You'}
                </div>
              </div>
              </div>
              {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 && (
              <div className="min-w-0 bg-gray-50 p-5">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({Array.isArray(detailsModal.attachments) ? detailsModal.attachments.length : 0})
                  </div>
                  <div className="flex items-center gap-3">
                    {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 && (
                      <>
                        <span className="text-xs text-gray-500">{attachmentIndex + 1} of {detailsModal.attachments.length}</span>
                        {/* Full screen toggle button beside Attachments label */}
                        <button
                          type="button"
                          onClick={() => setFullscreenAttachment(true)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50"
                          aria-label="View attachment in full screen"
                          title="View in full screen"
                        >
                          <Maximize2 className="h-3.5 w-3.5" />
                          Full Screen
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {Array.isArray(detailsModal.attachments) && detailsModal.attachments.length > 0 ? (
                  <div className="relative aspect-video overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <TicketAttachmentImage
                      key={detailsModal.attachments[attachmentIndex]?.attachmentId || attachmentIndex}
                      ticketId={detailsModal.ticketId}
                      attachment={detailsModal.attachments[attachmentIndex]}
                      alt={detailsModal.attachments[attachmentIndex]?.originalName || "attachment"}
                      className="h-full w-full object-contain"
                    />
                    {detailsModal.attachments.length > 1 && (
                      <>
                        <button type="button" aria-label="Previous attachment" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow hover:bg-white" onClick={() => setAttachmentIndex((index) => (index - 1 + detailsModal.attachments.length) % detailsModal.attachments.length)}>
                          <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button type="button" aria-label="Next attachment" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 text-gray-700 shadow hover:bg-white" onClick={() => setAttachmentIndex((index) => (index + 1) % detailsModal.attachments.length)}>
                          <ChevronRight className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                ) : null}
                {detailsModal.attachments[attachmentIndex]?.originalName && (
                  <div className="mt-2 truncate text-xs text-gray-500" title={detailsModal.attachments[attachmentIndex].originalName}>
                    {detailsModal.attachments[attachmentIndex].originalName}
                  </div>
                )}
              </div>
              )}
            </div>
            {/* Message shown as a full-width paragraph below the details/attachments */}
            <div className="border-t border-gray-200 p-5 text-left text-sm">
              <div className="text-xs uppercase tracking-wide text-gray-500">Message</div>
              <div className="mt-1 whitespace-pre-wrap break-words text-gray-700">{detailsModal.description || '-'}</div>
            </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Full-screen attachment viewer (opened via the "Full Screen" button) */}
      {fullscreenAttachment &&
        detailsModal &&
        Array.isArray(detailsModal.attachments) &&
        detailsModal.attachments.length > 0 && createPortal(
          <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/95"
            onClick={() => setFullscreenAttachment(false)}
          >
            {/* Top bar with filename + close */}
            <div
              className="absolute left-0 right-0 top-0 flex items-center justify-between gap-3 bg-gradient-to-b from-black/80 to-transparent px-4 py-3 text-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">
                  {detailsModal.attachments[attachmentIndex]?.originalName || "Attachment"}
                </div>
                <div className="text-xs text-white/70">
                  {attachmentIndex + 1} of {detailsModal.attachments.length} • Ticket #
                  {formatDisplayTicketId(detailsModal.createdAt, detailsModal.ticketId)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFullscreenAttachment(false)}
                aria-label="Close full screen"
                className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Image (clicking it does not close the viewer) */}
            <TicketAttachmentImage
              key={detailsModal.attachments[attachmentIndex]?.attachmentId || attachmentIndex}
              ticketId={detailsModal.ticketId}
              attachment={detailsModal.attachments[attachmentIndex]}
              alt={detailsModal.attachments[attachmentIndex]?.originalName || "attachment"}
              className="max-h-[85vh] max-w-[92vw] object-contain select-none"
              fallbackClassName="!h-auto !w-auto px-6 py-4 text-white/80"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Prev / Next navigation */}
            {detailsModal.attachments.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous attachment"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentIndex((index) =>
                      (index - 1 + detailsModal.attachments.length) %
                      detailsModal.attachments.length,
                    );
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white shadow hover:bg-white/25"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  aria-label="Next attachment"
                  onClick={(e) => {
                    e.stopPropagation();
                    setAttachmentIndex(
                      (index) => (index + 1) % detailsModal.attachments.length,
                    );
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/15 p-3 text-white shadow hover:bg-white/25"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Bottom thumbnail strip for quick jumps */}
            {detailsModal.attachments.length > 1 && (
              <div
                className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/80 to-transparent px-4 py-3"
                onClick={(e) => e.stopPropagation()}
              >
                {detailsModal.attachments.map((att, idx) => (
                  <button
                    key={att.attachmentId || idx}
                    type="button"
                    onClick={() => setAttachmentIndex(idx)}
                    className={`h-12 w-16 overflow-hidden rounded border-2 transition ${
                      idx === attachmentIndex
                        ? "border-white"
                        : "border-white/30 hover:border-white/60"
                    }`}
                    aria-label={`View attachment ${idx + 1}`}
                  >
                    <TicketAttachmentImage
                      ticketId={detailsModal.ticketId}
                      attachment={att}
                      alt={att.originalName || `attachment ${idx + 1}`}
                      className="h-full w-full object-cover"
                      fallbackClassName="text-white/80"
                      compact
                    />
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}

      {/* Delete / Close confirmation modal with reason */}
      {deleteConfirmModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              if (deletingTicketId !== deleteConfirmModal.ticketId) {
                setDeleteConfirmModal(null);
                setDeleteReason("");
                setDeleteError("");
              }
            }}
          />
          <div className="relative z-10 w-full max-w-3xl transform rounded-2xl bg-white p-7 sm:p-9 shadow-2xl transition-all border border-gray-100">
            <div className="flex items-center justify-between pb-5 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-rose-50 p-3.5 text-rose-600 border border-rose-200/60 shadow-sm">
                  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Delete Ticket</h3>
                  <p className="text-sm text-gray-500 font-mono mt-1">
                    #{formatDisplayTicketId(deleteConfirmModal.createdAt, deleteConfirmModal.ticketId)} • {deleteConfirmModal.title || "Support Request"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={deletingTicketId === deleteConfirmModal.ticketId}
                onClick={() => {
                  setDeleteConfirmModal(null);
                  setDeleteReason("");
                  setDeleteError("");
                }}
                className="rounded-full p-2.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <p className="text-base text-gray-600 leading-relaxed">
                Why are you closing this ticket? Please provide the reason below so our support team is aware.
              </p>

              <div>
                <label htmlFor="delete-reason-input" className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2.5">
                  Reason for Closing <span className="text-rose-500">*</span>
                </label>
                <textarea
                  ref={deleteInputRef}
                  id="delete-reason-input"
                  rows={6}
                  value={deleteReason}
                  onChange={(e) => {
                    setDeleteReason(e.target.value);
                    if (deleteError && e.target.value.trim()) {
                      setDeleteError("");
                    }
                  }}
                  disabled={deletingTicketId === deleteConfirmModal.ticketId}
                  placeholder="e.g., Issue resolved on my own, no longer encountered, solved with support..."
                  className={`w-full min-h-[160px] rounded-xl border p-4 sm:p-5 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition leading-relaxed ${
                    deleteError
                      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      : "border-gray-200 focus:border-rose-500 focus:ring-rose-100"
                  }`}
                />
                {deleteError && (
                  <p className="mt-2 text-sm text-rose-600 font-medium">
                    {deleteError}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3.5 pt-5 border-t border-gray-100">
              <button
                type="button"
                disabled={deletingTicketId === deleteConfirmModal.ticketId}
                className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition"
                onClick={() => {
                  setDeleteConfirmModal(null);
                  setDeleteReason("");
                  setDeleteError("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deletingTicketId === deleteConfirmModal.ticketId}
                onClick={handleConfirmDelete}
                className="inline-flex items-center gap-2.5 rounded-xl bg-rose-600 px-6 sm:px-7 py-2.5 text-base font-semibold text-white shadow-md hover:bg-rose-700 focus:outline-none disabled:opacity-60 transition"
              >
                {deletingTicketId === deleteConfirmModal.ticketId ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                )}
                Yes, Delete Ticket
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Reopen Ticket Confirmation Modal */}
      {reopenModal && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              if (updatingStatusId !== reopenModal.ticketId) {
                setReopenModal(null);
                setReopenReason("");
                setReopenError("");
              }
            }}
          />
          <div className="relative z-10 w-full max-w-3xl transform rounded-2xl bg-white p-7 sm:p-9 shadow-2xl transition-all border border-gray-100">
            <div className="flex items-center justify-between pb-5 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="rounded-xl bg-amber-50 p-3.5 text-amber-600 border border-amber-200/60 shadow-sm">
                  <RefreshCw className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Reopen Ticket</h3>
                  <p className="text-sm text-gray-500 font-mono mt-1">
                    #{formatDisplayTicketId(reopenModal.createdAt, reopenModal.ticketId)} • {reopenModal.title || "Support Request"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                disabled={updatingStatusId === reopenModal.ticketId}
                onClick={() => {
                  setReopenModal(null);
                  setReopenReason("");
                  setReopenError("");
                }}
                className="rounded-full p-2.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                aria-label="Close dialog"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 space-y-4">
              <p className="text-base text-gray-600 leading-relaxed">
                Please provide the reason why you want to reopen this ticket. Our support team will be notified with your explanation.
              </p>

              <div>
                <label htmlFor="reopen-reason-input" className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2.5">
                  Reason for Reopening <span className="text-rose-500">*</span>
                </label>
                <textarea
                  ref={reopenInputRef}
                  id="reopen-reason-input"
                  rows={6}
                  value={reopenReason}
                  onChange={(e) => {
                    setReopenReason(e.target.value);
                    if (reopenError && e.target.value.trim()) {
                      setReopenError("");
                    }
                  }}
                  disabled={updatingStatusId === reopenModal.ticketId}
                  placeholder="e.g., The issue reoccurred, further assistance required, or outcome was not fully resolved..."
                  className={`w-full min-h-[160px] rounded-xl border p-4 sm:p-5 text-base text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition leading-relaxed ${
                    reopenError
                      ? "border-rose-300 focus:border-rose-400 focus:ring-rose-100"
                      : "border-gray-200 focus:border-amber-500 focus:ring-amber-100"
                  }`}
                />
                {reopenError && (
                  <p className="mt-2 text-sm text-rose-600 font-medium">
                    {reopenError}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3.5 pt-5 border-t border-gray-100">
              <button
                type="button"
                disabled={updatingStatusId === reopenModal.ticketId}
                className="rounded-xl border border-gray-200 bg-white px-6 py-2.5 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:opacity-50 transition"
                onClick={() => {
                  setReopenModal(null);
                  setReopenReason("");
                  setReopenError("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={updatingStatusId === reopenModal.ticketId}
                onClick={handleConfirmReopen}
                className="inline-flex items-center gap-2.5 rounded-xl bg-amber-600 px-6 sm:px-7 py-2.5 text-base font-semibold text-white shadow-md hover:bg-amber-700 focus:outline-none disabled:opacity-60 transition"
              >
                {updatingStatusId === reopenModal.ticketId ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <RefreshCw className="h-5 w-5" />
                )}
                Confirm & Reopen
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const getStatusStyle = (status) => {
  switch (status) {
    case "OPEN":
      return "bg-blue-50 text-blue-700";
    case "PENDING":
      return "bg-amber-50 text-amber-700";
    case "RESOLVED":
      return "bg-emerald-50 text-emerald-700";
    case "CLOSED":
      return "bg-slate-100 text-slate-700";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

// Build a display Ticket ID: MMDDYYYY + first 4 chars of original id
const formatDisplayTicketId = (createdAt, originalId) => {
  try {
    const d = createdAt ? new Date(createdAt) : null;
    const mm = d ? String(d.getMonth() + 1).padStart(2, "0") : "";
    const dd = d ? String(d.getDate()).padStart(2, "0") : "";
    const yyyy = d ? String(d.getFullYear()) : "";
    const raw = String(originalId || "");
    const first4 = raw.replace(/[^a-zA-Z0-9]/g, "").slice(0, 4).toUpperCase();
    const datePart = `${mm}${dd}${yyyy}`;
    if (!datePart && !first4) return "-";
    return `${datePart}${first4}`;
  } catch (_) {
    return "-";
  }
};

export default UserSupportTickets;